import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, Plus, Minus, Info, LifeBuoy } from "lucide-react";
import { api } from "../lib/api.js";
import { LoadError } from "../components/LoadError.jsx";
// Opening a support session NARROWS this operator to one customer (see server/support.js);
// it is not an extra permission. The reason prompt is mandatory because the audit row is the
// whole point — "why were you in this account" has to have an answer.
async function openSupport(orgId, orgName) {
  const reason = window.prompt(`Open a support session for ${orgName}?

You will see only this customer's account until you close it. Reason (recorded in the audit log):`);
  if (!reason) return;
  try { await api.openSupport(orgId, reason); window.location.href = "/"; }
  catch (e) { alert(e.message); }
}
import { Spinner, timeAgo } from "../components/ui.jsx";

const gbp = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;
const CUR_SYM = { gbp: "£", usd: "$" };
const money2 = (pence, cur) => (CUR_SYM[cur] || "£") + ((pence || 0) / 100).toFixed(2);
const SUB_LABEL = { active: "Active", trialing: "Trialing", past_due: "Past due", suspended: "Suspended", canceled: "Canceled" };

const PAY_STATUS = {
  succeeded: { label: "succeeded", ok: true },
  processing: { label: "processing", ok: null },
  requires_payment_method: { label: "failed · no method", ok: false },
  requires_action: { label: "needs auth (3DS)", ok: null },
  requires_confirmation: { label: "unconfirmed", ok: null },
  canceled: { label: "canceled", ok: false },
};
const LEDGER_DESC = { topup: "Top-up", hardware_charge: "Plan charge", adjustment: "Manual adjustment", refund: "Refund", usage: "Metered usage" };
const AVATARS = ["#2563eb", "#0d9488", "#8b5cf6", "#d9822b", "#db2777", "#0891b2", "#65a30d"];

const s = {
  search: { width: 220, padding: "8px 12px 8px 33px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 13, fontFamily: "inherit", outline: "none" },
  btnSecondary: { display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 9, border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  btnPrimary: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 9, border: "1px solid var(--accent)", background: "var(--accent)", color: "var(--accent-contrast)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  btnDanger: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 9, border: "1px solid var(--err)", background: "var(--err)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  btnPrimarySm: { padding: "7px 15px", borderRadius: 8, border: "1px solid var(--accent)", background: "var(--accent)", color: "var(--accent-contrast)", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnGhostSm: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--text)", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textDecoration: "none" },
  card: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px 18px" },
  blockTitle: { fontSize: 11.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text-muted)" },
  statLabel: { fontSize: 11.5, color: "var(--text-muted)", fontWeight: 500 },
  statVal: { fontSize: 19, fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums", letterSpacing: "-.01em" },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 },
  input: { width: "100%", padding: "9px 12px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  amountInput: { width: "100%", padding: "9px 12px 9px 24px", borderRadius: 9, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 13, fontFamily: "var(--mono, monospace)", outline: "none", boxSizing: "border-box" },
};

function csvDownload(orgs) {
  const head = ["name", "slug", "balance_gbp", "members", "owners", "services", "databases", "status"];
  const rows = orgs.map((o) => [o.name, o.slug, ((o.balance_pence || 0) / 100).toFixed(2), o.members, o.owners, o.applications, o.databases, o.billing_status || "ok"]);
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "clients.csv"; a.click();
  URL.revokeObjectURL(url);
}

// Company details + verification for one client, with the operator's manual override.
// Verification is what the deploy gate checks, so "Mark verified" is how a number gets
// approved when there is no SMS provider (the client sees "we'll verify your number shortly").
const DETAIL_NAMES = { company: "company name", address: "address", country: "country", email: "email", phone: "phone" };
function ClientVerification({ orgId, orgName, reloadKey, onChange }) {
  const [ob, setOb] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null); // channel being changed
  const [actionErr, setActionErr] = useState(null);

  useEffect(() => {
    let off = false;
    setErr(null);
    api.adminOrgOnboarding(orgId)
      .then((d) => { if (!off) setOb(d && typeof d === "object" ? d : null); })
      .catch((e) => { if (!off) { setOb(null); setErr(e); } });
    return () => { off = true; };
  }, [orgId, reloadKey]);

  const setVerified = async (channel, verified) => {
    if (!verified && !window.confirm(`Revoke ${channel} verification for ${orgName}?\n\nThey will not be able to deploy, start or restart anything until it is verified again.`)) return;
    setBusy(channel); setActionErr(null);
    try {
      const next = await api.adminSetVerification(orgId, channel, verified);
      // The route returns the org's onboarding object; fall back to a re-read if it didn't.
      setOb(next && typeof next === "object" && (next.email || next.phone) ? next : await api.adminOrgOnboarding(orgId));
      onChange?.();
    } catch (e) { setActionErr(e); } finally { setBusy(null); }
  };

  const chip = (ok) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", background: ok ? "var(--ok-soft)" : "var(--warn-soft)", color: ok ? "var(--ok-text)" : "var(--warn-text)" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: ok ? "var(--ok)" : "var(--warn)" }} />{ok ? "Verified" : "Unverified"}
    </span>
  );
  const row = (label, value) => (
    <div style={{ minWidth: 0 }}>
      <div style={s.statLabel}>{label}</div>
      <div style={{ fontSize: 13, color: value ? "var(--text)" : "var(--text-muted)", marginTop: 3, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{value || "Not provided"}</div>
    </div>
  );
  const channelRow = (channel, label, value, st, extra) => (
    <div style={{ minWidth: 0 }}>
      <div style={s.statLabel}>{label}</div>
      <div style={{ fontSize: 13, color: value ? "var(--text)" : "var(--text-muted)", marginTop: 3, overflowWrap: "anywhere" }}>{value || "Not provided"}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 7 }}>
        {chip(!!st?.verified)}
        {st?.verified
          ? <button style={s.btnGhostSm} disabled={busy === channel} onClick={() => setVerified(channel, false)}>{busy === channel ? "…" : "Revoke"}</button>
          : <button style={s.btnGhostSm} disabled={busy === channel || !value} title={value ? undefined : `No ${label.toLowerCase()} on file`} onClick={() => setVerified(channel, true)}>{busy === channel ? "…" : "Mark verified"}</button>}
        {(extra || st?.verifiedAt) && <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{[extra, st?.verified && st?.verifiedAt ? timeAgo(st.verifiedAt) : null].filter(Boolean).join(" · ")}</span>}
      </div>
    </div>
  );

  const d = ob?.details || {};
  const missing = (Array.isArray(ob?.missing) ? ob.missing : []).map((m) => DETAIL_NAMES[m] || m);
  return (
    <div style={{ ...s.card, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={s.blockTitle}>Company details &amp; verification</div>
        {ob && (ob.exempt
          ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: "var(--neutral-soft)", color: "var(--neutral-text)" }}>Exempt</span>
          : <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 999, background: ob.complete ? "var(--ok-soft)" : "var(--warn-soft)", color: ob.complete ? "var(--ok-text)" : "var(--warn-text)" }}>{ob.complete ? "Complete — can deploy" : "Incomplete — deploys blocked"}</span>)}
      </div>
      {err ? (
        <p style={{ margin: 0, fontSize: 12.5, color: "var(--err-text)" }}>Couldn't load this client's verification status: {err.message}</p>
      ) : !ob ? <Spinner /> : (
        <>
          <div className="client-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {row("Company", d.company)}
            {row("Country", d.country)}
            {row("Address", d.address)}
            {row("VAT number", d.vat)}
            {channelRow("email", "Email", ob.email?.address || d.email, ob.email, ob.email?.verified && ob.email?.viaSignIn ? "through their sign-in" : null)}
            {channelRow("phone", "Phone", ob.phone?.number || d.phone, ob.phone, ob.phone?.verified
              ? ((ob.phone.verifiedMethod || ob.phone.method) === "manual" ? "verified by hand" : (ob.phone.verifiedMethod || ob.phone.method) === "sms" ? "by SMS code" : null)
              : ob.phone?.reviewRequested ? "client has asked for this number to be checked" : ob.phone?.method === "manual" ? "no SMS provider — verify by hand" : null)}
          </div>
          {missing.length > 0 && <p style={{ margin: "12px 0 0", fontSize: 12.5, color: "var(--warn-text)" }}>Still missing: {missing.join(", ")}.</p>}
          {ob.billing && !ob.billing.ready && <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>Billing not ready{ob.billing.reason ? ` — ${ob.billing.reason}` : ""}.</p>}
          {actionErr && <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--err-text)" }}>{actionErr.message}</p>}
        </>
      )}
    </div>
  );
}

function AccountCard({ org, color, expanded, onToggle, onChange }) {
  const [spend, setSpend] = useState(null);
  const [spendToday, setSpendToday] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [payments, setPayments] = useState(null);
  const [resources, setResources] = useState(null);
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [billing, setBilling] = useState(null);
  const [curBusy, setCurBusy] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [subUrl, setSubUrl] = useState(null);
  const [compBusy, setCompBusy] = useState(false);
  const [discInput, setDiscInput] = useState("0");
  const [billingErr, setBillingErr] = useState(null);
  const [svcPlans, setSvcPlans] = useState(null); // { services:[{uuid,name,cpus,memory,plan_id,detected_plan_id}], plans:[] }
  const [svcBusy, setSvcBusy] = useState(null);   // uuid being assigned
  const [projects, setProjects] = useState(null); // this client's folders
  const [transferUuid, setTransferUuid] = useState(null);
  const [transferCandidates, setTransferCandidates] = useState(null);
  const [transferTargets, setTransferTargets] = useState([]);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferDbUuids, setTransferDbUuids] = useState([]);
  const [confirmNoDatabase, setConfirmNoDatabase] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);
  const [verifyKey, setVerifyKey] = useState(0); // bump to re-read verification after details change

  // A failed money request must never render as a number. Substituting 0 for a failed
  // usage call showed £0.00 spend for a client who owes you, and a failed payments call
  // rendered "Stripe is not configured" — a statement about YOUR setup, from a fetch error.
  // These now hold null and the UI says it could not load, which is the truth.
  const [spendErr, setSpendErr] = useState(null);
  const [panelErr, setPanelErr] = useState(null);

  useEffect(() => {
    const refresh = () => api.adminOrgUsage(org.id).then((u) => {
      setSpend(u.totalPence);
      const today = new Date().toISOString().slice(0, 10);
      setSpendToday((u.daily || []).find((d) => d.day === today)?.pence ?? 0);
      setSpendErr(null);
    }).catch((e) => { setSpend(null); setSpendToday(null); setSpendErr(e); });
    refresh();
    const timer = setInterval(refresh, 30_000);
    return () => clearInterval(timer);
  }, [org.id]);
  useEffect(() => {
    if (!expanded || wallet) return;
    setPanelErr(null);
    const note = (e) => setPanelErr((prev) => prev || e);
    api.adminOrgWallet(org.id).then(setWallet).catch((e) => { setWallet(null); note(e); });
    api.adminOrgPayments(org.id).then(setPayments).catch((e) => { setPayments(null); note(e); });
    api.adminOrgResources(org.id).then(setResources).catch((e) => { setResources(null); note(e); });
    api.adminOrgProjects(org.id).then((d) => setProjects(d.projects || [])).catch((e) => { setProjects(null); note(e); });
    api.adminOrgBillingInfo(org.id).then(setInfo).catch((e) => { setInfo(null); note(e); });
    api.orgBilling(org.id).then(setBilling).catch(setBillingErr);
    api.orgServicePlans(org.id).then(setSvcPlans).catch(() => setSvcPlans(null));
  }, [expanded]); // eslint-disable-line
  useEffect(() => {
    if (!expanded) return;
    const refreshWallet = () => api.adminOrgWallet(org.id).then(setWallet).catch((e) => setPanelErr((prev) => prev || e));
    const timer = setInterval(refreshWallet, 30_000);
    return () => clearInterval(timer);
  }, [expanded, org.id]);
  useEffect(() => { if (billing?.comp) setDiscInput(String(billing.comp.discountPct ?? 0)); }, [billing]);

  const pastDue = org.billing_status === "arrears" || org.balance_pence < 0;
  const balancePos = (org.balance_pence || 0) > 0;

  const adjust = async (sign) => {
    const pounds = Number(amount);
    if (!Number.isFinite(pounds) || pounds <= 0) return;
    setBusy(true);
    try {
      await api.adminAdjustCredit(org.id, { amount_pence: sign * Math.round(pounds * 100), notes: notes || null });
      setAmount(""); setNotes("");
      setWallet(await api.adminOrgWallet(org.id));
      onChange?.();
    } finally { setBusy(false); }
  };
  // Changing the email or phone un-verifies that channel, so re-read verification afterwards.
  const saveInfo = async () => { setSavingInfo(true); try { setInfo(await api.adminSaveBillingInfo(org.id, info)); setVerifyKey((k) => k + 1); onChange?.(); } catch (e) { alert(e.message); } finally { setSavingInfo(false); } };
  // Credit any paid Stripe top-ups the webhook never delivered, then refresh the ledger.
  const reconcile = async () => {
    setBusy(true);
    try {
      await api.adminReconcileTopups(org.id);
      setWallet(await api.adminOrgWallet(org.id));
      onChange?.();
    } finally { setBusy(false); }
  };
  const setCur = async (c) => { if (billing?.currency === c) return; setCurBusy(true); try { await api.setOrgCurrency(org.id, c); setBilling(await api.orgBilling(org.id)); } finally { setCurBusy(false); } };
  const subscribe = async () => { setSubBusy(true); try { const r = await api.subscribeOrg(org.id); setSubUrl(r.url); window.open(r.url, "_blank", "noopener"); } catch (e) { alert(e.message); } finally { setSubBusy(false); } };
  const saveComp = async (patch) => { setCompBusy(true); try { setBilling({ ...billing, comp: await api.setOrgComp(org.id, patch) }); } catch (e) { alert(e.message); } finally { setCompBusy(false); } };
  const planName = (id) => svcPlans?.plans.find((p) => p.id === id)?.name || id;
  const assignPlan = async (uuid, planId, kind = "service") => {
    setSvcBusy(uuid);
    try {
      await (kind === "database" ? api.setDatabasePlan(uuid, planId) : api.setServicePlan(uuid, planId));
      const [sp, res] = await Promise.all([api.orgServicePlans(org.id), api.adminOrgResources(org.id)]);
      setSvcPlans(sp); setResources(res); onChange?.();
    } catch (e) { alert(e.message); } finally { setSvcBusy(null); }
  };
  const openTransfer = async (uuid) => {
    setTransferBusy(true);
    try {
      const [candidates, targets] = await Promise.all([api.adminTransferCandidates(org.id), api.adminTransferTargets()]);
      const service = candidates.services.find((s) => s.uuid === uuid);
      setTransferCandidates(candidates);
      setTransferTargets(targets.filter((t) => t.org_id !== org.id));
      setTransferTarget("");
      setTransferDbUuids(service?.linkedDatabaseUuids || []);
      setConfirmNoDatabase(false);
      setTransferUuid(uuid);
    } catch (e) { alert(e.message); }
    finally { setTransferBusy(false); }
  };
  const transferService = async () => {
    if (!transferUuid || !transferTarget) return;
    setTransferBusy(true);
    try {
      await api.adminTransferService(org.id, {
        service_uuid: transferUuid,
        target_email: transferTarget,
        database_uuids: transferDbUuids,
        confirm_no_database: confirmNoDatabase,
      });
      setTransferUuid(null);
      setTransferCandidates(null);
      const [sp, res] = await Promise.all([api.orgServicePlans(org.id), api.adminOrgResources(org.id)]);
      setSvcPlans(sp); setResources(res); onChange?.();
    } catch (e) { alert(e.message); }
    finally { setTransferBusy(false); }
  };
  const subTotal = (billing?.lines || []).reduce((sum, l) => sum + l.unitAmountMinor * l.quantity, 0);

  // Metered usage is based on sampled running time and updated on each health tick.
  const accrued = spend ?? 0;
  const planTotal = resources?.monthly_total_pence ?? 0;
  // There is no free tier. A client with no plan total either has nothing deployed, or has
  // services with no paid plan attached — never "Free".
  const nothingDeployed = (org.applications || 0) + (org.databases || 0) === 0;

  return (
    <div style={{ border: pastDue ? "1px solid var(--err)" : "1px solid var(--border)", borderRadius: 13, background: "var(--surface)", boxShadow: "var(--shadow)", overflow: "hidden" }}>
      {/* Collapsed header */}
      <div role="button" onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 13, padding: "15px 18px", cursor: "pointer" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: "transform .15s", transform: expanded ? "rotate(90deg)" : "none", flexShrink: 0 }}><path d="m9 18 6-6-6-6" /></svg>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{(org.name || "?")[0].toUpperCase()}</span>
        <div style={{ minWidth: 0, maxWidth: 260 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{org.name}</span>
            {pastDue && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 999, background: "var(--err-soft)", color: "var(--err-text)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", flexShrink: 0 }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--err)" }} />Past due</span>}
            {org.onboarding?.complete === false && <span title="Company details or verification unfinished — this client cannot deploy" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 999, background: "var(--warn-soft)", color: "var(--warn-text)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", flexShrink: 0 }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--warn)" }} />Incomplete</span>}
          </div>
          <span style={{ display: "block", fontFamily: "var(--mono, monospace)", fontSize: 11.5, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{org.slug}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexShrink: 0, marginLeft: "auto" }}>
          {org.economics && (
            <span title="Monthly: what this client is billed, against what their plans cost DebutDeploy (the plans' share of the hardware)" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 118 }}>
              <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--text)", whiteSpace: "nowrap" }}>
                {gbp(org.economics.billedPence)} <span style={{ fontWeight: 500, color: org.economics.marginPence < 0 ? "var(--err-text)" : "var(--text-muted)" }}>/ {gbp(org.economics.costPence)}</span>
              </span>
              <span style={{ fontSize: 9.5, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 }}>Billed / cost pm</span>
            </span>
          )}
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 70 }}>
            <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: pastDue ? "var(--err-text)" : balancePos ? "var(--ok-text)" : "var(--text)" }}>{gbp(org.balance_pence)}</span>
            <span style={{ fontSize: 9.5, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 }}>Credit</span>
          </span>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 74 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{spendErr ? "—" : spend == null ? "…" : gbp(spend)}</span>
            <span style={{ fontSize: 9.5, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 }}>Usage today</span>
          </span>
          <span style={{ fontSize: 11.5, color: "var(--text-muted)", minWidth: 58, textAlign: "right", whiteSpace: "nowrap" }}>{org.created_at ? timeAgo(org.created_at) : "—"}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid var(--border)", padding: "20px 22px 22px", background: "var(--surface-2)" }}>
          {/* Summary line */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 18px", marginBottom: 18, fontSize: 12.5, color: "var(--text-muted)" }}>
            <span>Balance <strong style={{ color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{gbp(org.balance_pence)}</strong></span>
            <span style={{ width: 1, height: 12, background: "var(--border-strong)" }} />
            <span>{org.members} member{org.members !== 1 ? "s" : ""} · {org.owners} owner{org.owners !== 1 ? "s" : ""}</span>
            <span style={{ width: 1, height: 12, background: "var(--border-strong)" }} />
            <span>{org.applications} services · {org.databases} databases</span>
            {payments?.customer && <><span style={{ width: 1, height: 12, background: "var(--border-strong)" }} /><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Stripe <span style={{ fontFamily: "var(--mono, monospace)", fontSize: 11.5, color: "var(--text)", background: "var(--surface)", border: "1px solid var(--border)", padding: "1px 7px", borderRadius: 6 }}>{payments.customer}</span></span></>}
          </div>

          {panelErr && (
            <div style={{ marginBottom: 14 }}>
              <LoadError error={panelErr} what="this client's billing" />
            </div>
          )}

          {/* Plan + Usage */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div style={s.card}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}><div style={s.blockTitle}>Plan</div><span style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{resources == null ? "—" : gbp(planTotal)}<span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>/mo</span></span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ display: "inline-flex", padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: planTotal > 0 ? "var(--accent-soft)" : nothingDeployed ? "var(--neutral-soft)" : "var(--warn-soft)", color: planTotal > 0 ? "var(--accent-text)" : nothingDeployed ? "var(--neutral-text)" : "var(--warn-text)" }}>{resources == null ? "Unknown" : planTotal > 0 ? "Active" : nothingDeployed ? "No services" : "No paid plan"}</span>
                <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{nothingDeployed ? "Nothing deployed" : `${org.applications} services · ${org.databases} databases`}</span>
              </div>
            </div>
            <div style={s.card}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}><div style={s.blockTitle}>Usage today</div><span style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>{spendErr ? "—" : spendToday == null ? "…" : gbp(spendToday)}</span></div>
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>{spendErr ? "Couldn't load usage." : accrued > 0 ? "Compute, bandwidth & storage." : "No metered usage yet."}</p>
            </div>
          </div>

          {/* This client's folders. Drill-down from Clients rather than a flat cross-org
              list — that stops being readable once there are more than a handful. */}
          <div style={{ ...s.card, marginBottom: 14 }}>
            <div style={{ ...s.blockTitle, marginBottom: 12 }}>Folders</div>
            {projects == null ? (
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>Loading…</p>
            ) : projects.length === 0 ? (
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>No folders — this client owns nothing yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {projects.map((p) => (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Link to={`/admin/projects/${p.id}`} style={{ fontSize: 13.5, color: "var(--text)", fontWeight: 500, textDecoration: "none" }}>
                      {p.name}
                    </Link>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{p.slug}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Services & plans — detect the tier from live limits, assign with one click */}
          {(svcPlans?.services?.length > 0 || svcPlans?.databases?.length > 0) && (
            <div style={{ ...s.card, marginBottom: 14 }}>
              <div style={{ ...s.blockTitle, marginBottom: 12 }}>Services &amp; plans</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                {svcPlans.services.map((sv) => {
                  const hasLimits = sv.cpus && String(sv.cpus) !== "0";
                  const specs = hasLimits ? `${sv.cpus} vCPU · ${sv.memory}` : "limits unset";
                  return (
                    <div key={sv.uuid} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ minWidth: 0, flex: "1 1 170px" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sv.name}</div>
                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                          {specs}{!sv.plan_id && sv.detected_plan_id && <span style={{ color: "var(--accent-text)" }}> · detected {planName(sv.detected_plan_id)}</span>}
                        </div>
                      </div>
                      <select value={sv.plan_id || ""} disabled={svcBusy === sv.uuid} onChange={(e) => assignPlan(sv.uuid, e.target.value || null)} style={{ ...s.input, width: 168 }}>
                        <option value="" disabled>{sv.detected_plan_id ? `Detected: ${planName(sv.detected_plan_id)}` : "Choose a paid plan…"}</option>
                        {svcPlans.plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.priceMo}/mo</option>)}
                      </select>
                      {!sv.plan_id && sv.detected_plan_id && (
                        <button style={s.btnGhostSm} disabled={svcBusy === sv.uuid} onClick={() => assignPlan(sv.uuid, sv.detected_plan_id)}>
                          {svcBusy === sv.uuid ? "…" : `Assign ${planName(sv.detected_plan_id)}`}
                        </button>
                      )}
                      <button style={s.btnGhostSm} disabled={transferBusy} onClick={() => openTransfer(sv.uuid)}>
                        {transferBusy && transferUuid === sv.uuid ? "Loading…" : "Transfer service"}
                      </button>
                    </div>
                  );
                })}
                {/* Databases bill too; one with no plan is running free, so say so. */}
                {(svcPlans.databases || []).map((d) => (
                  <div key={d.uuid} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0, flex: "1 1 170px" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                      <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                        Database{d.engine ? ` · ${d.engine}` : ""}{d.status ? ` · ${d.status}` : ""}
                        {!d.plan_id && <span style={{ color: "var(--warn-text)", fontWeight: 600 }}> · No plan — running free</span>}
                      </div>
                    </div>
                    <select value={d.plan_id || ""} disabled={svcBusy === d.uuid} onChange={(e) => assignPlan(d.uuid, e.target.value || null, "database")} style={{ ...s.input, width: 168 }}>
                      <option value="" disabled>Choose a paid plan…</option>
                      {(svcPlans.dbPlans || []).map((p) => <option key={p.id} value={p.id}>{p.name} — ${p.priceMo}/mo</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {transferUuid && transferCandidates &&<div style={{ marginTop: 14, padding: 14, border: "1px solid var(--border)", borderRadius: 9, background: "var(--surface-2)" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>Transfer {svcPlans.services.find((sv) => sv.uuid === transferUuid)?.name || "service"}</div>
                <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "0 0 10px" }}>The service and selected linked databases move together into the target account’s Production folder. Coolify infrastructure is unchanged.</p>
                <label style={s.label}>Destination account</label>
                <select value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)} style={{ ...s.input, maxWidth: 440, marginBottom: 10 }}>
                  <option value="">Choose an account…</option>
                  {transferTargets.map((t) => <option key={t.id} value={t.email}>{t.org_name} · {t.name || t.email}</option>)}
                </select>
                <div style={{ ...s.label, marginTop: 2 }}>Databases to move with this service</div>
                {transferCandidates.databases.length === 0 ? <p style={{ fontSize: 12, color: "var(--text-muted)" }}>No databases belong to this account.</p> : transferCandidates.databases.map((d) => {
                  const linked = transferCandidates.services.find((sv) => sv.uuid === transferUuid)?.linkedDatabaseUuids.includes(d.uuid);
                  const checked = transferDbUuids.includes(d.uuid);
                  return <label key={d.uuid} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12.5, color: "var(--text)" }}>
                    <input type="checkbox" checked={checked} disabled={linked} onChange={(e) => setTransferDbUuids((old) => e.target.checked ? [...new Set([...old, d.uuid])] : old.filter((id) => id !== d.uuid))} />
                    {d.name}{linked && <span style={{ color: "var(--accent-text)" }}>· auto-linked from env or saved link</span>}
                  </label>;
                })}
                {transferDbUuids.length === 0 && <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 12.5, color: "var(--text)" }}><input type="checkbox" checked={confirmNoDatabase} onChange={(e) => setConfirmNoDatabase(e.target.checked)} /> This service has no attached database.</label>}
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}><button style={s.btnPrimarySm} disabled={transferBusy || !transferTarget || (!transferDbUuids.length && !confirmNoDatabase)} onClick={transferService}>{transferBusy ? "Transferring…" : "Transfer service and database(s)"}</button><button style={s.btnGhostSm} disabled={transferBusy} onClick={() => setTransferUuid(null)}>Cancel</button></div>
              </div>}
            </div>
          )}

          {/* Subscription & currency */}
          <div style={{ ...s.card, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={s.blockTitle}>Subscription &amp; currency</div>
              <div style={{ display: "flex", gap: 3, background: "var(--surface-2)", borderRadius: 999, padding: 3, border: "1px solid var(--border)" }}>
                {["gbp", "usd"].map((c) => {
                  const active = billing?.currency === c;
                  return (
                    <button key={c} disabled={!billing || curBusy || active} onClick={() => setCur(c)}
                      style={{ fontSize: 12, fontWeight: 600, padding: "4px 13px", borderRadius: 999, border: "none", cursor: active || !billing ? "default" : "pointer", background: active ? "var(--text)" : "transparent", color: active ? "var(--surface)" : "var(--text-muted)", fontFamily: "inherit" }}>
                      {c.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
            {billingErr ? (
              <p style={{ fontSize: 12.5, color: "var(--err-text)" }}>Couldn't load billing details: {billingErr.message}</p>
            ) : !billing ? <Spinner /> : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 14 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={s.statLabel}>Subscription</span><span style={s.statVal}>{SUB_LABEL[billing.subscription?.status] || "None"}</span></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={s.statLabel}>Monthly service</span><span style={s.statVal}>{money2(subTotal, billing.currency)}</span></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={s.statLabel}>Min top-up</span><span style={s.statVal}>{money2(billing.min_topup_pence, billing.currency)}</span></div>
                </div>
                <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
                  <button style={{ ...s.btnPrimary, opacity: (subBusy || !billing.lines?.length) ? 0.55 : 1 }} disabled={subBusy || !billing.lines?.length} onClick={subscribe}>
                    {subBusy ? "Creating link…" : billing.subscription?.status === "active" ? "New checkout link" : "Start subscription"}
                  </button>
                  {subUrl && <a style={s.btnGhostSm} href={subUrl} target="_blank" rel="noreferrer">Open checkout ↗</a>}
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{billing.lines?.length ? "Monthly plan fees go to the card. This does not add account credit; wallet top-ups are separate." : "No priced services to subscribe."}</span>
                </div>
                {/* Operator override: comp (100% free, skips the deploy gate) or a 0–99% discount. */}
                <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                  <span style={s.statLabel}>Override</span>
                  <button disabled={compBusy} onClick={() => saveComp({ comp: !billing.comp?.comp })}
                    style={{ fontSize: 12, fontWeight: 600, padding: "5px 13px", borderRadius: 999, border: "1px solid var(--border)", cursor: compBusy ? "default" : "pointer", background: billing.comp?.comp ? "var(--text)" : "transparent", color: billing.comp?.comp ? "var(--surface)" : "var(--text-muted)", fontFamily: "inherit" }}>
                    {billing.comp?.comp ? "Comp — free ✓" : "Comp (free)"}
                  </button>
                  {!billing.comp?.comp && (
                    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input type="number" min="0" max="99" value={discInput} disabled={compBusy}
                        onChange={(e) => setDiscInput(e.target.value)} style={{ ...s.input, width: 64 }} />
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>% off</span>
                      <button style={s.btnGhostSm} disabled={compBusy} onClick={() => saveComp({ discountPct: Number(discInput) })}>Apply</button>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Unbilled */}
          <div style={{ ...s.card, marginBottom: 14 }}>
            <div style={{ ...s.blockTitle, marginBottom: 14 }}>Metered usage <span style={{ fontWeight: 500, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>· month to date</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={s.statLabel}>Today</span><span style={s.statVal}>{spendErr ? "—" : spendToday == null ? "…" : gbp(spendToday)}</span></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={s.statLabel}>Month to date</span><span style={s.statVal}>{spendErr ? "—" : spend == null ? "…" : gbp(accrued)}</span></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}><span style={s.statLabel}>Monthly service plans</span><span style={s.statVal}>{gbp(planTotal)}</span></div>
            </div>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", borderRadius: 9, background: "var(--accent-soft)", fontSize: 12, color: "var(--accent-text)", lineHeight: 1.5 }}><Info size={15} style={{ flexShrink: 0, marginTop: 1 }} /><span>For prepaid accounts, running compute is metered by the hour and deducted from account credit once a day, shortly after midnight UTC, for the day just ended. Stripe subscriptions continue to charge monthly plan fees to the card. Disk and mailbox fees remain monthly wallet charges for prepaid accounts.</span></div>
          </div>

          {/* Ledger + Adjust */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14, alignItems: "start" }}>
            <div style={s.card}>
              <div style={{ ...s.blockTitle, marginBottom: 12 }}>Credit ledger</div>
              {!wallet && <Spinner />}
              {wallet && (wallet.recent_ledger || []).length === 0 && <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>No credit transactions yet.</p>}
              {wallet && (wallet.recent_ledger || []).map((l) => (
                <div key={l.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ minWidth: 0 }}><div style={{ fontSize: 12.5, color: "var(--text)", fontWeight: 500 }}>{l.notes || LEDGER_DESC[l.type] || l.type}</div><div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{l.created_at ? timeAgo(l.created_at) : ""} · {l.by || "system"}</div></div>
                  <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums", fontFamily: "var(--mono, monospace)", color: l.amount_pence < 0 ? "var(--text)" : "var(--ok-text)" }}>{l.amount_pence < 0 ? "−" : "+"}{gbp(Math.abs(l.amount_pence))}</span>
                </div>
              ))}
            </div>
            <div style={s.card}>
              <div style={{ ...s.blockTitle, marginBottom: 12 }}>Adjust credit <span style={{ fontWeight: 500, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>· manual</span></div>
              <div style={{ display: "flex", gap: 9, marginBottom: 11 }}>
                <div style={{ position: "relative", width: 120, flexShrink: 0 }}><span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--text-muted)", pointerEvents: "none" }}>£</span><input placeholder="0.00" style={s.amountInput} value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
                <input placeholder="Reason (comp, refund…)" style={s.input} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 9 }}>
                <button style={s.btnPrimary} disabled={busy} onClick={() => adjust(+1)}><Plus size={14} /> Add credit</button>
                <button style={s.btnDanger} disabled={busy} onClick={() => adjust(-1)}><Minus size={14} /> Debit</button>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div style={{ ...s.card, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={s.blockTitle}>Stripe payment attempts</div>
              <button style={s.btnGhostSm} disabled={busy} onClick={reconcile} title="Credit any paid top-ups the webhook missed">Reconcile top-ups</button>
            </div>
            {!payments && <Spinner />}
            {payments && payments.payments.length === 0 && <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>{payments.configured === false ? "Stripe is not configured." : payments.customer ? "No payment attempts on record." : "No Stripe customer yet — this client hasn't started a top-up."}</p>}
            {payments && payments.payments.map((p) => {
              const st = PAY_STATUS[p.status] || { label: p.status, ok: null };
              const col = st.ok === true ? "var(--ok-text)" : st.ok === false ? "var(--err-text)" : "var(--text-muted)";
              const dot = st.ok === true ? "var(--ok)" : st.ok === false ? "var(--err)" : "var(--text-muted)";
              return (
                <div key={p.id} style={{ display: "grid", gridTemplateColumns: "82px 92px 160px 1fr", gap: 14, alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{p.created ? timeAgo(p.created) : "—"}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", fontVariantNumeric: "tabular-nums", fontFamily: "var(--mono, monospace)" }}>{gbp(p.amount_pence)}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 600, color: col }}><span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: dot }} />{st.label}</span>
                  <span style={{ fontSize: 12.5, color: st.ok === false ? "var(--err-text)" : "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.error || (st.ok === true ? "Card payment · top-up" : "")}</span>
                </div>
              );
            })}
          </div>

          <ClientVerification orgId={org.id} orgName={org.name} reloadKey={verifyKey} onChange={onChange} />

          {/* Billing info */}
          <div style={s.card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <div style={s.blockTitle}>Billing information</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={s.btnGhostSm} onClick={() => openSupport(org.id, org.name)}
                  title="See this customer's services and databases as they do, for troubleshooting. Time-boxed, read-only, and audited.">
                  <LifeBuoy size={13} /> Open account
                </button>
                <a style={s.btnGhostSm} href={api.adminInvoiceUrl(org.id)} target="_blank" rel="noreferrer"><FileText size={13} /> View invoices</a>
                <button style={s.btnPrimarySm} disabled={savingInfo || !info} onClick={saveInfo}>{savingInfo ? "Saving…" : "Save"}</button>
              </div>
            </div>
            {!info && <Spinner />}
            {info && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><label style={s.label}>Billing email</label><input value={info.billing_email || ""} placeholder="billing@company.com" style={s.input} onChange={(e) => setInfo({ ...info, billing_email: e.target.value })} /></div>
                <div><label style={s.label}>Company name</label><input value={info.billing_company || ""} placeholder="Company Ltd" style={s.input} onChange={(e) => setInfo({ ...info, billing_company: e.target.value })} /></div>
                <div><label style={s.label}>Phone</label><input value={info.billing_phone || ""} placeholder="+44 7700 900123" style={s.input} onChange={(e) => setInfo({ ...info, billing_phone: e.target.value })} /></div>
                <div><label style={s.label}>Country</label><input value={info.billing_country || ""} placeholder="United Kingdom" style={s.input} onChange={(e) => setInfo({ ...info, billing_country: e.target.value })} /></div>
                <div><label style={s.label}>VAT number</label><input value={info.billing_vat || ""} placeholder="GB 000 0000 00" style={s.input} onChange={(e) => setInfo({ ...info, billing_vat: e.target.value })} /></div>
                <div><label style={s.label}>Billing address</label><textarea value={info.billing_address || ""} placeholder="Address" style={{ ...s.input, minHeight: 38, resize: "vertical" }} onChange={(e) => setInfo({ ...info, billing_address: e.target.value })} /></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Clients() {
  const [orgs, setOrgs] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => api.adminOrgs().then(setOrgs).catch(setError);
  // The real server bill, for comparison with the plans' allocated cost. Optional context:
  // if it can't be read the strip simply leaves that tile out.
  const [infra, setInfra] = useState(null);
  useEffect(() => { api.billing().then((d) => setInfra(d?.infra || null)).catch(() => {}); }, []);
  // Block body → effect returns undefined; passing `load` directly returned its
  // Promise as the effect cleanup, which React calls on unmount → "r is not a function".
  useEffect(() => {
    load();
    const refresh = setInterval(load, 30_000);
    return () => clearInterval(refresh);
  }, []);

  if (error) return <div className="page"><p style={{ color: "var(--err)" }}>Failed: {error.message}</p></div>;
  if (!orgs) return <div className="flex h-40 items-center justify-center" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading…</div>;

  const isPastDue = (o) => o.billing_status === "arrears" || o.balance_pence < 0;
  const counts = { all: orgs.length, pastdue: orgs.filter(isPastDue).length, active: orgs.filter((o) => (o.balance_pence || 0) > 0).length };
  const filterDefs = [["all", "All clients"], ["pastdue", "Past due"], ["active", "In credit"]];
  const shown = orgs
    .filter((o) => filter === "all" ? true : filter === "pastdue" ? isPastDue(o) : (o.balance_pence || 0) > 0)
    .filter((o) => !q || (o.name + " " + o.slug).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="page client-billing-page" style={{ maxWidth: 1080 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 25, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text)" }}>Billing</h1>
          <p style={{ margin: 0, fontSize: 13.5, color: "var(--text-muted)" }}>Client organizations — prepaid credit, plans, usage and Stripe payment activity.</p>
        </div>
        <div className="client-billing-toolbar" style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Search size={15} style={{ position: "absolute", left: 11, pointerEvents: "none", color: "var(--text-muted)" }} />
            <input placeholder="Search clients" style={s.search} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button style={s.btnSecondary} onClick={() => csvDownload(shown)}><FileText size={14} /> Export CSV</button>
        </div>
      </div>

      <EconomicsStrip orgs={orgs} infra={infra} />

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
        {filterDefs.map(([id, label]) => {
          const active = filter === id;
          return (
            <button key={id} onClick={() => setFilter(id)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "2px 0", border: "none", background: "transparent", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, cursor: "pointer", color: active ? "var(--text)" : "var(--text-muted)", borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent" }}>
              {label}
              {counts[id] > 0 && <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, background: active ? "var(--accent-soft)" : "var(--neutral-soft)", color: active ? "var(--accent-text)" : "var(--neutral-text)", fontSize: 11, fontWeight: 700 }}>{counts[id]}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {shown.length === 0 && <p style={{ fontSize: 13, color: "var(--text-muted)" }}>No clients match.</p>}
        {shown.map((o, i) => (
          <AccountCard
            key={o.id} org={o} color={AVATARS[i % AVATARS.length]}
            expanded={!!expanded[o.id]}
            onToggle={() => setExpanded((e) => ({ ...e, [o.id]: !e[o.id] }))}
            onChange={load}
          />
        ))}
      </div>
    </div>
  );
}

// Per month, across every client: what is billed, what those plans cost DebutDeploy, and
// what Comp gives away — with the real server bill alongside, because the plans' cost is
// their allocated share of the hardware and says nothing about capacity sitting idle.
function EconomicsStrip({ orgs, infra }) {
  const rows = orgs.filter((o) => o.economics);
  if (!rows.length) return null;
  const sum = (k) => rows.reduce((n, o) => n + (o.economics[k] || 0), 0);
  const billed = sum("billedPence"), cost = sum("costPence"), list = sum("listPence");
  const margin = billed - cost, comped = Math.max(0, list - billed);
  const tile = { flex: "1 1 150px", minWidth: 0, padding: "13px 15px", border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)" };
  const Tile = ({ label, value, hint, tone }) => (
    <div style={tile} title={hint}>
      <div style={s.statLabel}>{label}</div>
      <div style={{ ...s.statVal, color: tone || "var(--text)" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{hint}</div>
    </div>
  );
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
      <Tile label="Billed to clients" value={`${gbp(billed)}/mo`} hint="Plans, disks and mailboxes actually charged" />
      <Tile label="Cost to DebutDeploy" value={`${gbp(cost)}/mo`} hint="Those plans' share of the hardware" />
      <Tile label="Margin" value={`${gbp(margin)}/mo`} hint="Billed less plan cost" tone={margin < 0 ? "var(--err-text)" : "var(--ok-text)"} />
      <Tile label="Given away as Comp" value={`${gbp(comped)}/mo`} hint="List price of comped and discounted services" />
      {infra && Number(infra.totalMonthly) > 0 && (
        <Tile label="Actual server bill" value={`€${Number(infra.totalMonthly).toFixed(2)}/mo`} hint={`What the ${infra.servers?.length || 0} servers really cost, idle capacity included`} />
      )}
    </div>
  );
}
